import { id } from "@ethersproject/hash"
import type { BigNumberish } from "@ethersproject/bignumber"
import invariant from "tiny-invariant"
import { useQueryClient } from "@tanstack/react-query"

import { useAddress } from "web3/hooks/useAddress"
import { getChainIdParams } from "web3/helpers/chainId"
import { getChainReferenceName } from "web3/helpers/chainReference"
import type {
  GovernorContract,
  Governor as GovernorQuery,
  AccountId,
  Proposal,
} from "query/graphql"
import {
  ProposalActionType,
  GovernorType,
  useCreateProposalActionAttemptMutation,
} from "query/graphql"
import { useNormalizeProposalId } from "proposal/hooks/useNormalizeProposalId"
import { useCorrectChain } from "web3/hooks/useCorrectChain"
import { useGovernorAlpha } from "web3/hooks/useGovernorAlpha"
import { useGovernorBravo } from "web3/hooks/useGovernorBravo"
import { useGovernorOpenZeppelin } from "web3/hooks/useGovernorOpenZeppelin"
import { useTransaction } from "web3/providers/TransactionProvider"
import type { TransactionOnMining } from "web3/types/transaction"
import { TransactionToastState } from "web3/types/transaction"
import { useToast } from "common/hooks/useToast"
import { useMe } from "user/providers/MeProvider"
import { useChainReference } from "web3/hooks/useChainReference"
import { useSafeTransactionV2 } from "safe/hooks/useSafeTransactionV2"
import { useSignerStore } from "web3/providers/SignerProvider"
import { useGovernorHub } from "web3/hooks/useGovernorHub"

type Governor = Pick<GovernorQuery, "id" | "chainId" | "timelockId"> & {
  contracts: {
    governor: Pick<GovernorContract, "type" | "address">
  }
}

type Values = {
  queueProposal: () => Promise<void>
}

type Metadata = {
  proposalId: BigNumberish
  governanceId: AccountId
  governorAddress: string
  type: ProposalActionType
}

type Props = {
  governance: Governor
  proposal: Proposal
}

export const useQueueProposal = ({ proposal, governance }: Props): Values => {
  const { toast } = useToast()
  const me = useMe()
  const address = useAddress()
  const queryClient = useQueryClient()

  const canUseSafeTx = me?.type === "SAFE"

  const { onchainId, id: internalId } = proposal

  const { chainId, contracts, id: governanceId } = governance
  const { address: governorAddress, type: governorType } = contracts.governor

  const { reference } = getChainIdParams(chainId)
  const chainReferenceName = getChainReferenceName(reference)

  const chainReference = useChainReference()
  const { signer } = useSignerStore()
  const { isCorrectChain } = useCorrectChain({ chainId, chainReference })
  const { proposalId } = useNormalizeProposalId({
    proposalId: onchainId ?? "",
    governanceId,
  })

  const governorAlpha = useGovernorAlpha(governorAddress, signer)
  const governorBravo = useGovernorBravo(governorAddress, signer)
  const governorOz = useGovernorOpenZeppelin(governorAddress, signer)
  const governorHub = useGovernorHub(governorAddress, signer)

  const {
    proposeMultiGnosisTransaction,
    buildSafeTransaction,
    onPendingSafeTx,
  } = useSafeTransactionV2(
    {
      safeId: me?.id as string,
      accountAddress: address,
    },
    { enabled: canUseSafeTx },
  )

  const onMining: TransactionOnMining<Metadata> = ({
    transaction,
    metadata,
  }) => {
    if (address) {
      const { hash } = transaction
      const { proposalId, type } = metadata

      if (type === ProposalActionType.Queue) {
        createProposalActionAttempt({
          input: {
            actor: address,
            proposalId: proposalId as string,
            txHash: hash,
            type: ProposalActionType.Queue,
          },
        })
      }
    }
  }

  const handlePendingSafeTx = (values: Record<string, string>) => {
    if (
      !values.safe ||
      !values.safeTxHash ||
      !values.governanceId ||
      !values.proposalId ||
      !address
    ) {
      return
    }

    if (values.type === ProposalActionType.Queue) {
      createProposalActionAttempt({
        input: {
          actor: address,
          proposalId: internalId,
          txHash: values.safeTxHash,
          type: ProposalActionType.Queue,
        },
      })
    }
  }

  const options = {
    on: {
      [TransactionToastState.Mining]: onMining,
    },
  }
  const { send, setMetadata } = useTransaction<Metadata>(options)

  const { mutate: createProposalActionAttempt } =
    useCreateProposalActionAttemptMutation({
      onSuccess: (data) => {
        if (data) {
          const queriesToInvalidate = ["ProposalActionAttempt"]
          queriesToInvalidate.forEach((query) =>
            queryClient.invalidateQueries([query]),
          )
        }
      },
    })

  const queueProposal = async (): Promise<void> => {
    if (!isCorrectChain) {
      toast({
        status: "error",
        title: "Incorrect network",
        description: `Connect to ${chainReferenceName} to queue this proposal`,
        duration: 10000,
      })

      return
    }

    const { executableCalls, metadata } = proposal

    const calldatas = executableCalls?.map((call) => call.calldata) ?? []
    const targets = executableCalls?.map((call) => call.target) ?? []
    const values = executableCalls?.map((call) => call.value) ?? []

    const descriptionHash = id(metadata.description)

    switch (governorType) {
      case GovernorType.Governoralpha:
        invariant(governorAlpha, "governorAlpha factory is not defined")

        setMetadata({
          proposalId: internalId,
          governanceId,
          governorAddress,
          type: ProposalActionType.Queue,
        })

        if (canUseSafeTx) {
          const safeTxHash = await proposeMultiGnosisTransaction([
            buildSafeTransaction(
              governorAlpha,
              "queue",
              [proposalId],
              governorAddress,
            ),
          ])
          onPendingSafeTx(safeTxHash, governanceId, {
            requiredParameters: ["proposalId"],
            expectedMethod: "queue",
          })((values) =>
            handlePendingSafeTx({
              ...values,
              type: ProposalActionType.Queue,
              proposalId,
            }),
          )
        } else {
          send(() => governorAlpha.queue(proposalId))
        }

        break

      case GovernorType.Nounsfork:
      case GovernorType.Governorbravo:
        invariant(governorBravo, "governorBravo factory is not defined")

        setMetadata({
          proposalId: internalId,
          governanceId,
          governorAddress,
          type: ProposalActionType.Queue,
        })

        if (canUseSafeTx) {
          const safeTxHash = await proposeMultiGnosisTransaction([
            buildSafeTransaction(
              governorBravo,
              "queue",
              [proposalId],
              governorAddress,
            ),
          ])
          onPendingSafeTx(safeTxHash, governanceId, {
            requiredParameters: ["proposalId"],
            expectedMethod: "queue",
          })((values) =>
            handlePendingSafeTx({
              ...values,
              type: ProposalActionType.Queue,
              proposalId,
            }),
          )
        } else {
          send(() => governorBravo.queue(proposalId))
        }

        break

      case GovernorType.Openzeppelingovernor:
        invariant(governorOz, "governorOz factory is not defined")

        setMetadata({
          proposalId: internalId,
          governanceId,
          governorAddress,
          type: ProposalActionType.Queue,
        })

        if (canUseSafeTx) {
          const safeTxHash = await proposeMultiGnosisTransaction([
            buildSafeTransaction(
              governorOz,
              "queue",
              [targets, values, calldatas, descriptionHash],
              governorAddress,
            ),
          ])
          onPendingSafeTx(safeTxHash, governanceId, {
            requiredParameters: [
              "targets",
              "values",
              "calldatas",
              "descriptionHash",
            ],
            expectedMethod: "queue",
          })((values) =>
            handlePendingSafeTx({
              ...values,
              type: ProposalActionType.Queue,
              proposalId,
            }),
          )
        } else {
          send(() =>
            governorOz.queue(targets, values, calldatas, descriptionHash),
          )
        }

        break

      case GovernorType.Hub:
        invariant(governorHub, "governorHub factory is not defined")

        setMetadata({
          proposalId: internalId,
          governanceId,
          governorAddress,
          type: ProposalActionType.Queue,
        })

        if (canUseSafeTx) {
          const safeTxHash = await proposeMultiGnosisTransaction([
            buildSafeTransaction(
              governorHub,
              "queue",
              [targets, values, calldatas, descriptionHash],
              governorAddress,
            ),
          ])
          onPendingSafeTx(safeTxHash, governanceId, {
            requiredParameters: [
              "targets",
              "values",
              "calldatas",
              "descriptionHash",
            ],
            expectedMethod: "queue",
          })((values) =>
            handlePendingSafeTx({
              ...values,
              type: ProposalActionType.Queue,
              proposalId,
            }),
          )
        } else {
          send(() =>
            governorHub.queue(targets, values, calldatas, descriptionHash),
          )
        }

        break
      default:
        break
    }
  }

  return { queueProposal }
}
